/**
 * Product Insights builder (SCRUM-208), ported from Ryan's QUICK_INSIGHTS.
 * Each insight pairs an intelligence signal with a product and surfaces the
 * customers who both have that signal active AND are eligible for the product.
 *
 * NOTE: Ryan keyed products by prototype id (e.g. "p_40120"); our API keys by
 * SKU, so the seed pairings reference SKUs.
 */
import type { Customer } from '@/features/intelligence-dashboard/customer.schema';
import type { Product } from '@/features/product-scan/product-scan.schema';

import { INSIGHT_TYPES } from '../campaigns-config';
import { customerEligibility } from '../customer-eligibility';
import type { InsightType } from '@/features/intelligence-dashboard/insight.types';

interface Pairing {
  insightKey: InsightType;
  sku: string;
  rationale: string;
}

const PAIRINGS: Pairing[] = [
  {
    insightKey: 'flight',
    sku: '40120',
    rationale:
      'Retain at-risk deposits with a competitive money market yield before balances move.',
  },
  {
    insightKey: 'smb',
    sku: '40112',
    rationale: 'Reach SMBs in growth mode with a Small Business Checking account as they scale.',
  },
  {
    insightKey: 'relgap',
    sku: '40330',
    rationale:
      'Cross-sell IRA to thin-relationship customers building long-term retirement savings.',
  },
  {
    insightKey: 'relgap',
    sku: '40210',
    rationale: 'Add a Smart Savings account to checking-only customers to deepen the relationship.',
  },
  {
    insightKey: 'dormant',
    sku: '402101',
    rationale: 'Reactivate dormant 50+ customers with our Regular Savings 50 Plus offer.',
  },
];

export interface QuickInsight {
  id: string;
  insightKey: InsightType;
  insightLabel: string;
  product: Product;
  /**
   * Static fallback rationale rendered while the AI fetch is in-flight or
   * when the gateway is unavailable. Replaced by the live AI content once
   * `useRationale` resolves with `aiGenerated: true` (SCRUM-51).
   */
  rationale: string;
  /**
   * Set to `true` once the AI rationale endpoint confirms live content.
   * `undefined` while loading; `false` on gateway unavailability.
   * Optional so existing tests that use `satisfies QuickInsight` continue
   * to compile without changes.
   */
  aiGenerated?: boolean;
  customerIds: string[];
  count: number;
  avgBalance: number;
  avgMatchPct: number;
}

/**
 * Builds the ranked Product Insights from the live customers + products.
 * Keeps insights matching ≥5 customers, sorts by count × avg-match, takes 6
 * (Ryan's exact thresholds — typically 5 qualify).
 */
export function buildQuickInsights(customers: Customer[], products: Product[]): QuickInsight[] {
  return PAIRINGS.map((pair) => {
    const product = products.find((p) => p.sku === pair.sku);
    if (!product) return null;

    const matches = customers.filter((c) => {
      if (!c.insights[pair.insightKey]?.active) return false;
      const e = customerEligibility(c, product);
      if (!e.forcedAllMet) return false;
      return e.met >= product.requiredMatches;
    });

    const count = matches.length;
    const totalBalance = matches.reduce((s, c) => s + c.balance, 0);
    const avgBalance = count ? Math.round(totalBalance / count) : 0;
    const avgMatchPct = count
      ? Math.round(
          (matches.reduce((s, c) => {
            const e = customerEligibility(c, product);
            return s + e.met / e.total;
          }, 0) /
            count) *
            100,
        )
      : 0;

    return {
      id: `${pair.insightKey}_${pair.sku}`,
      insightKey: pair.insightKey,
      insightLabel: INSIGHT_TYPES[pair.insightKey].short,
      product,
      rationale: pair.rationale,
      customerIds: matches.map((c) => c.id),
      count,
      avgBalance,
      avgMatchPct,
    } satisfies QuickInsight;
  })
    .filter((qi): qi is QuickInsight => qi !== null && qi.count >= 5)
    .sort((a, b) => b.count * b.avgMatchPct - a.count * a.avgMatchPct)
    .slice(0, 6);
}
